// NMC Compliance Check Engine
// Derives 11 checks from wizard form state per NMC Gazette 2022

const NON_MEDICAL_DEPTS = ["Anatomy","Physiology","Biochemistry","Pharmacology","Microbiology"];

const PG_DEPT_MAP = {
  "MD Anatomy": ["Anatomy"],
  "MD Physiology": ["Physiology"],
  "MD Biochemistry": ["Biochemistry"],
  "MD Pharmacology": ["Pharmacology"],
  "MD Microbiology": ["Microbiology"],
  "MD Pathology": ["Pathology","Forensic Medicine and Toxicology"],
  "MD Community Medicine": ["Community Medicine"],
  "MD Forensic Medicine": ["Forensic Medicine and Toxicology","Pathology"],
  "MD Medicine": ["General Medicine"],
  "MD General Medicine": ["General Medicine"],
  "MD Paediatrics": ["Paediatrics"],
  "MD Anaesthesiology": ["Anaesthesiology"],
  "MD Radio-Diagnosis": ["Radio-Diagnosis"],
  "MD Radio Diagnosis": ["Radio-Diagnosis"],
  "MD Dermatology": ["Dermatology, Venereology and Leprosy"],
  "MD Psychiatry": ["Psychiatry"],
  "MD Respiratory Medicine": ["Respiratory Medicine"],
  "MD Pulmonary Medicine": ["Respiratory Medicine"],
  "MS General Surgery": ["General Surgery"],
  "MS Orthopaedics": ["Orthopaedics"],
  "MS Ophthalmology": ["Ophthalmology"],
  "MS ENT": ["Oto-Rhino-Laryngology (ENT)"],
  "MS Otorhinolaryngology": ["Oto-Rhino-Laryngology (ENT)"],
  "MS OBG": ["Obstetrics & Gynaecology"],
  "MS Obstetrics & Gynaecology": ["Obstetrics & Gynaecology"],
  "DNB Medicine": ["General Medicine"],
  "DNB Surgery": ["General Surgery"],
  "DNB Paediatrics": ["Paediatrics"],
  "DNB Anaesthesiology": ["Anaesthesiology"],
  "DM Cardiology": ["General Medicine"],
  "DM Neurology": ["General Medicine"],
  "DM Nephrology": ["General Medicine"],
  "DM Gastroenterology": ["General Medicine"],
  "MCh Cardiothoracic Surgery": ["General Surgery"],
  "MCh Neurosurgery": ["General Surgery"],
  "MCh Urology": ["General Surgery"],
  "MCh Plastic Surgery": ["General Surgery"],
};

const EXP_THRESHOLDS = {
  "Senior Resident":      { postPG: 0, priorYears: 0, priorRole: null },
  "Assistant Professor":   { postPG: 1, priorYears: 1, priorRole: "Senior Resident" },
  "Associate Professor":   { postPG: 5, priorYears: 4, priorRole: "Assistant Professor" },
  "Professor":             { postPG: 8, priorYears: 3, priorRole: "Associate Professor" },
};

function calcAge(dob, refDate) {
  if (!dob || !refDate) return null;
  const d = new Date(dob);
  const r = new Date(refDate);
  let age = r.getFullYear() - d.getFullYear();
  const m = r.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && r.getDate() < d.getDate())) age--;
  return age;
}

function sumExpYears(pastExp, designation, nmcOnly) {
  let total = 0;
  for (const p of (pastExp || [])) {
    if (designation && p.designation !== designation) continue;
    if (p.nature_of_employment !== "Full-time") continue;
    if (nmcOnly) {
      const medTypes = ["Government Medical College","Private Medical College","Deemed-to-be-University Medical College","AIIMS / Institute of National Importance"];
      if (!medTypes.includes(p.institution_type)) continue;
      if (p.nmc_recognised !== "Yes") continue;
    }
    if (p.from_date && p.to_date) {
      const from = new Date(p.from_date);
      const to = new Date(p.to_date);
      total += (to - from) / (365.25 * 86400000);
    }
  }
  return Math.round(total * 10) / 10;
}

export function runComplianceChecks(form) {
  const checks = [];
  const desig = form.designation || "";
  const dept = form.department || "";
  const ugCourse = form.ug_course || "";
  const pgTitle = form.pg_title || "";
  const pgCategory = form.pg_category || "";
  const joinDate = form.joined_at || "";
  const dob = form.dob || "";
  const ageAtJoin = calcAge(dob, joinDate);
  const pgYear = form.pg_complete_year ? Number(form.pg_complete_year) : null;
  const joinYear = joinDate ? new Date(joinDate).getFullYear() : null;
  const postPGYears = (pgYear && joinYear) ? joinYear - pgYear : null;

  // 4.1 Age at Appointment
  if (ageAtJoin !== null) {
    let status = "Pass", expected = "Under 70 years";
    if (ageAtJoin >= 70) status = "Fail";
    else if (ageAtJoin >= 65) status = "Warning";
    checks.push({ check_id: "age_at_appointment", category: "Age & Eligibility", name: "Age at Appointment",
      status, severity: "hard", expected, actual: `${ageAtJoin} years`, nmc_reference: "§ Age limits", fix_step: 0 });
  } else {
    checks.push({ check_id: "age_at_appointment", category: "Age & Eligibility", name: "Age at Appointment",
      status: "Warning", severity: "soft", expected: "Under 70 years", actual: "DOB or Joining Date missing", nmc_reference: "§ Age limits", fix_step: 0 });
  }

  // 4.2 Senior Resident Age Cap
  if (desig === "Senior Resident") {
    const st = (ageAtJoin !== null && ageAtJoin < 45) ? "Pass" : (ageAtJoin !== null && ageAtJoin >= 45) ? "Fail" : "Warning";
    checks.push({ check_id: "sr_age_cap", category: "Age & Eligibility", name: "Senior Resident Age Cap",
      status: st, severity: "hard", expected: "Under 45 years at appointment", actual: ageAtJoin !== null ? `${ageAtJoin} years` : "Age unknown", nmc_reference: "§ Senior Resident", fix_step: 2 });
  } else {
    checks.push({ check_id: "sr_age_cap", category: "Age & Eligibility", name: "Senior Resident Age Cap",
      status: "Pass", severity: "hard", expected: "N/A (not Senior Resident)", actual: desig || "—", nmc_reference: "§ Senior Resident", fix_step: 2 });
  }

  // 4.3 UG-Department Compatibility
  const isMBBS = ugCourse === "MBBS";
  const isBDS = ugCourse === "BDS";
  const isNonMed = !isMBBS && !isBDS && ugCourse;
  let ugStatus = "Pass", ugActual = `${ugCourse || "—"} + ${dept || "—"}`;
  if (isBDS && dept && dept !== "Dentistry") { ugStatus = "Fail"; }
  else if (isNonMed && dept && !NON_MEDICAL_DEPTS.includes(dept)) { ugStatus = "Fail"; }
  else if (!ugCourse) { ugStatus = "Warning"; ugActual = "UG course not set"; }
  checks.push({ check_id: "ug_dept_compat", category: "Qualifications & Designation Compatibility", name: "UG-Department Compatibility",
    status: ugStatus, severity: ugStatus === "Fail" ? "hard" : "soft", expected: isMBBS ? "MBBS → any dept" : isBDS ? "BDS → Dentistry only" : isNonMed ? `Non-medical → ${NON_MEDICAL_DEPTS.join("/")} only` : "—",
    actual: ugActual, nmc_reference: "Tables 1A, 2 — Non-medical faculty", fix_step: 4 });

  // 4.4 PG Required for Designation
  const needsPG = ["Senior Resident","Assistant Professor","Associate Professor","Professor","Professor-HOD"].includes(desig);
  const hasPG = pgTitle.trim().length > 0 || pgCategory;
  let pgReqStatus = "Pass";
  if (needsPG && !hasPG) pgReqStatus = "Fail";
  if (["Tutor","Demonstrator","Junior Resident"].includes(desig)) pgReqStatus = "Pass";
  checks.push({ check_id: "pg_required", category: "Qualifications & Designation Compatibility", name: "PG Required for Designation",
    status: pgReqStatus, severity: "hard", expected: needsPG ? "PG qualification required" : "PG not required", actual: hasPG ? (pgTitle || pgCategory) : "No PG entered",
    nmc_reference: "Table 1A — Qualification requirements", fix_step: 4 });

  // 4.5 PG Specialty Matches Department
  if (hasPG && dept) {
    const pgKey = pgTitle.trim();
    let matched = false;
    for (const [key, depts] of Object.entries(PG_DEPT_MAP)) {
      if (pgKey.toLowerCase().includes(key.toLowerCase().replace("MD ","").replace("MS ","").replace("DNB ","")) ||
          key.toLowerCase().includes(pgKey.toLowerCase())) {
        if (depts.some(d => dept.includes(d) || d.includes(dept))) { matched = true; break; }
      }
    }
    // Also do a direct partial match
    if (!matched && (dept.toLowerCase().includes(pgKey.toLowerCase().replace(/^(md|ms|dnb|dm|mch)\s+/i,"")) ||
        pgKey.toLowerCase().replace(/^(md|ms|dnb|dm|mch)\s+/i,"").includes(dept.toLowerCase()))) matched = true;
    checks.push({ check_id: "pg_dept_match", category: "Qualifications & Designation Compatibility", name: "PG Specialty Matches Department",
      status: matched ? "Pass" : "Warning", severity: "soft", expected: `PG aligned with ${dept}`, actual: pgKey,
      nmc_reference: "§ Departmental qualifications", fix_step: 4 });
  } else {
    checks.push({ check_id: "pg_dept_match", category: "Qualifications & Designation Compatibility", name: "PG Specialty Matches Department",
      status: "Pass", severity: "soft", expected: "—", actual: "Not applicable", nmc_reference: "§ Departmental qualifications", fix_step: 4 });
  }

  // 4.6 Post-PG Experience
  const threshold = EXP_THRESHOLDS[desig];
  if (threshold && postPGYears !== null) {
    const priorYrs = threshold.priorRole ? sumExpYears(form.past_experience, threshold.priorRole, true) : 999;
    const postPGOk = postPGYears >= threshold.postPG;
    const priorOk = !threshold.priorRole || priorYrs >= threshold.priorYears;
    let expStatus = (postPGOk && priorOk) ? "Pass" : "Fail";
    if (expStatus === "Fail" && postPGYears >= threshold.postPG - 1 && priorYrs >= threshold.priorYears - 1) expStatus = "Warning";
    const expActual = `${postPGYears}y post-PG` + (threshold.priorRole ? `, ${priorYrs}y as ${threshold.priorRole} (NMC-recognised)` : "");
    const expExpected = `≥${threshold.postPG}y post-PG` + (threshold.priorRole ? `, ≥${threshold.priorYears}y as ${threshold.priorRole}` : "");
    checks.push({ check_id: "post_pg_experience", category: "Experience", name: "Post-PG Experience for Designation",
      status: expStatus, severity: expStatus === "Fail" ? "hard" : "soft", expected: expExpected, actual: expActual,
      nmc_reference: "Table 1A — Promotion criteria", fix_step: 5 });
  } else {
    checks.push({ check_id: "post_pg_experience", category: "Experience", name: "Post-PG Experience for Designation",
      status: threshold ? "Warning" : "Pass", severity: "soft", expected: threshold ? `≥${threshold.postPG}y post-PG` : "—", actual: postPGYears !== null ? `${postPGYears}y post-PG` : "PG year or joining date missing",
      nmc_reference: "Table 1A — Promotion criteria", fix_step: 5 });
  }

  // 4.7 UG Registration on File
  const ugRegOk = form.ug_reg_date && form.ug_council;
  const ugRegNA = isNonMed;
  checks.push({ check_id: "ug_registration", category: "Registration", name: "UG Registration on File",
    status: ugRegOk || ugRegNA ? "Pass" : "Fail", severity: "hard",
    expected: ugRegNA ? "Non-medical (equivalent record)" : "UG Council + Registration Date",
    actual: ugRegOk ? `${form.ug_council}, ${form.ug_reg_date}` : ugRegNA ? "Non-medical faculty" : "Missing",
    nmc_reference: "§ Registration — State/National Medical Register", fix_step: 4 });

  // 4.8 PG Registration on File
  if (needsPG && hasPG) {
    const pgRegOk = form.pg_reg_date && form.pg_council;
    checks.push({ check_id: "pg_registration", category: "Registration", name: "PG Registration on File",
      status: pgRegOk ? "Pass" : "Fail", severity: "hard",
      expected: "PG Council + Registration Date", actual: pgRegOk ? `${form.pg_council}, ${form.pg_reg_date}` : "Missing",
      nmc_reference: "§ Registration — State/National Medical Register", fix_step: 4 });
  } else {
    checks.push({ check_id: "pg_registration", category: "Registration", name: "PG Registration on File",
      status: "Pass", severity: "hard", expected: "Not required", actual: "—",
      nmc_reference: "§ Registration", fix_step: 4 });
  }

  // 4.9 DNB Equivalence
  const isDNB = form.has_dnb || (pgTitle && pgTitle.toUpperCase().startsWith("DNB"));
  if (isDNB) {
    const bedCount = form.dnb_bed_count ? Number(form.dnb_bed_count) : null;
    let dnbSt = "Pass";
    if (bedCount === null) dnbSt = "Warning";
    else if (bedCount < 500) dnbSt = "Warning";
    checks.push({ check_id: "dnb_equivalence", category: "Qualifications & Designation Compatibility", name: "DNB Equivalence",
      status: dnbSt, severity: "soft",
      expected: "DNB from 500+ bed institution for full MD/MS equivalence",
      actual: bedCount !== null ? `${bedCount} beds` : "Bed count unknown",
      nmc_reference: "§ 4 DNB Equivalence", fix_step: 4 });
  } else {
    checks.push({ check_id: "dnb_equivalence", category: "Qualifications & Designation Compatibility", name: "DNB Equivalence",
      status: "Pass", severity: "soft", expected: "N/A (not DNB)", actual: "—",
      nmc_reference: "§ 4 DNB Equivalence", fix_step: 4 });
  }

  // 4.10 MET Course Status
  const needsMET = ["Associate Professor","Professor","Professor-HOD","Dean"].includes(desig);
  checks.push({ check_id: "met_course", category: "Continuing Education & Output", name: "MET Course Status",
    status: form.met_done || !needsMET ? "Pass" : "Warning", severity: "soft",
    expected: needsMET ? "MET completed (required for Assoc Prof+)" : "Not required at this level",
    actual: form.met_done ? "Completed" : "Not completed",
    nmc_reference: "§ MET — within 1 year", fix_step: 7 });

  // 4.11 BMR Course Status
  const needsBMR = needsMET;
  checks.push({ check_id: "bmr_course", category: "Continuing Education & Output", name: "BMR Course Status",
    status: form.bmr_done || !needsBMR ? "Pass" : "Warning", severity: "soft",
    expected: needsBMR ? "BMR completed (required for Assoc Prof+)" : "Not required at this level",
    actual: form.bmr_done ? "Completed" : "Not completed",
    nmc_reference: "§ BMR — within 1 year", fix_step: 7 });

  return checks;
}
